import React from 'react'
import PersonAvatar from './PersonAvatar'
import type { PersonLikeData } from '../utils/people'

/**
 * Shared enriched picker-row content: primary text, an optional
 * differentiating-fields subtitle (see `utils/pickerSubtitles`), a
 * right-aligned muted usage line ("14 check-ins · Aug 3"), and the existing
 * avatar/picture handling. Used by the composer's `UnifiedSelector` person
 * rows and the checkin-edit `ReferenceSelector` rows.
 *
 * Purely presentational: the caller owns the interactive container (click
 * handlers, selected/hover classes), so both pickers keep their existing
 * selection styling.
 */

export interface PickerRowUsage {
    checkinCount: number
    lastTaggedAt?: string | null
}

interface PickerRowProps {
    primary: React.ReactNode
    /** Extra classes on the primary line (e.g. `font-medium`, selection tint). */
    primaryClassName?: string
    /** Differentiating-fields subtitle; empty/undefined renders nothing. */
    subtitle?: string
    /** Usage line, right-aligned and muted. Omitted entirely when undefined
     *  or when `checkinCount` is 0. */
    usage?: PickerRowUsage
    /** Person-like data → `PersonAvatar` (picture or initials fallback).
     *  Takes precedence over `pictureUrl`. */
    avatarData?: PersonLikeData | null
    /** Plain rectangular picture, for generic collection rows. */
    pictureUrl?: string
    /** Rendered at the far right, after the usage block (type badge,
     *  selected checkmark). */
    trailing?: React.ReactNode
    className?: string
}

/** "Aug 3" for the current year, "Aug 3, 2023" otherwise; null if unparsable.
 *  Date-only strings are parsed as local dates (bare `new Date('2026-08-03')`
 *  is UTC midnight, which can shift a day in western timezones). */
function formatUsageDate(iso: string): string | null {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso)
    const date = match
        ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
        : new Date(iso)
    if (isNaN(date.getTime())) return null
    const sameYear = date.getFullYear() === new Date().getFullYear()
    return date.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        ...(sameYear ? {} : { year: 'numeric' }),
    })
}

/** "14 check-ins · Aug 3" / "1 check-in"; null when there's nothing to show. */
export function formatUsageLine(usage: PickerRowUsage | undefined): string | null {
    if (!usage || usage.checkinCount <= 0) return null
    const countText = `${usage.checkinCount} check-in${usage.checkinCount === 1 ? '' : 's'}`
    const dateText = usage.lastTaggedAt ? formatUsageDate(usage.lastTaggedAt) : null
    return dateText ? `${countText} · ${dateText}` : countText
}

const PickerRow: React.FC<PickerRowProps> = ({
    primary,
    primaryClassName = '',
    subtitle,
    usage,
    avatarData,
    pictureUrl,
    trailing,
    className = '',
}) => {
    const usageLine = formatUsageLine(usage)

    return (
        <div className={`flex items-center gap-2 min-w-0 w-full ${className}`}>
            {avatarData ? (
                <PersonAvatar
                    data={avatarData}
                    className="h-7 w-7 flex-shrink-0"
                    initialsClassName="text-[10px]"
                />
            ) : pictureUrl ? (
                <img
                    src={pictureUrl}
                    alt=""
                    className="w-10 h-10 object-cover rounded flex-shrink-0"
                />
            ) : null}
            <div className="flex-1 min-w-0">
                <div className={`truncate ${primaryClassName}`}>{primary}</div>
                {subtitle && (
                    <div className="text-xs text-gray-500 truncate">{subtitle}</div>
                )}
            </div>
            {usageLine && (
                <div className="ml-2 text-xs text-gray-400 whitespace-nowrap shrink-0 text-right">
                    {usageLine}
                </div>
            )}
            {trailing}
        </div>
    )
}

export default PickerRow
